import { MessageInputArea } from 'Components/Chat/ChatFooter/MessageInputArea';
import { MobXProviderContext, observer } from 'mobx-react';

import { ContactModelBase } from 'Models/ContactModel';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { usePersonStore } from 'Modules/person/index.store';
import React from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { routeChecks } from 'Utils/routeUtils';
import { Styled } from './index.styles';

export const ChatFooter: React.FC = observer(() => {
  const { conversation, participant, person } =
    React.useContext<RootStoreProps>(MobXProviderContext);

  // Keep optOutPhoneNumbersInfo here so the component updates when it changes
  const { optOutPhoneNumbersInfo, getOptOutPhoneNumberInfo, selectedContacts } =
    useConversationStore();
  const { campaignEnabled10Dlc } = usePersonStore();

  const isCreatingAdhocGroup = window.location.pathname.includes('adHocGroup');

  let otherParticipantsNumbers: string[] = [];

  if (isCreatingAdhocGroup && conversation.adHocGroupParticipants?.length) {
    otherParticipantsNumbers = conversation.adHocGroupParticipants
      .filter(
        (contact) =>
          contact instanceof ContactModelBase ||
          (contact.phoneNumber && contact.source !== 'Bhive')
      )
      .map((participant) => participant.phoneNumber);
  } else if (conversation?.CurrentConversation?.participants?.length) {
    otherParticipantsNumbers = conversation.CurrentConversation.participants
      .filter(
        ({ personId, phone }) => personId !== person.loggedInPersonId && phone
      )
      .map((participant) => participant.phone);
  }

  const optInParticipants = otherParticipantsNumbers.filter((phoneNumber) => {
    return getOptOutPhoneNumberInfo(phoneNumber) === false;
  });

  const optOutParticipants = otherParticipantsNumbers.filter((phoneNumber) => {
    return getOptOutPhoneNumberInfo(phoneNumber) === true;
  });

  const canCreateGroupWithOptinMembers =
    optOutParticipants?.length >= 1 && optInParticipants?.length >= 2;

  const createGroupWithOptinMembers = () => {
    conversation.setChannelInfoDetails(
      conversation.channelInfoDetails.channelConversationId,
      'new',
      'phone'
    );
    void conversation.handleAdHocGroups(
      optInParticipants.map((participant) => participant)
    );
  };

  const isGroupChat =
    conversation.CurrentConversation?.grouping === 'Channel' ||
    conversation.CurrentConversation?.grouping === 'Group';

  // If the current conversation is a group chat, but the user is not part of it anymore (this check has priority over the following ones)
  if (isGroupChat && !conversation.CurrentConversation?.isActiveParticipant) {
    return (
      <Styled.MessageInputAreaDisabled>
        <Styled.BodyCopy>
          You can't send messages to this group because you are not a member
          anymore.
        </Styled.BodyCopy>
      </Styled.MessageInputAreaDisabled>
    );
  }

  // If there's at least one member of the current conversation (current user ignored) that has chosen to opt-out...
  if (optOutParticipants?.length > 0) {
    // ...and it's a group chat
    if (isGroupChat || isCreatingAdhocGroup) {
      return (
        <Styled.MessageInputAreaDisabled>
          <Styled.BodyCopy>
            You can't text this group because at least one member has opted-out
            from receiving messages.
            {canCreateGroupWithOptinMembers && (
              <Styled.CenteredTextLink
                testid="createGroupOptinMembers-textLink"
                onClick={createGroupWithOptinMembers}
              >
                Create a group with the remaining members
              </Styled.CenteredTextLink>
            )}
          </Styled.BodyCopy>
        </Styled.MessageInputAreaDisabled>
      );
      // ...and it's a one on one conversation
    } else {
      return (
        <Styled.MessageInputAreaDisabled>
          <Styled.BodyCopy>
            You can't text this number because they opted-out from receiving
            messages.
          </Styled.BodyCopy>
        </Styled.MessageInputAreaDisabled>
      );
    }
  }

  // The otherParticipantsNumbers contains the contacts being selected on the top search dropdown.
  // The selectedContacts contains the contacts being selected on the create conversation page.
  const isSMSConversation =
    otherParticipantsNumbers.length > 0 ||
    (selectedContacts.length > 0 &&
      selectedContacts.some(
        (contact) =>
          contact.type === 'unsaved-contact' ||
          contact.type === 'external-contact'
      ));

  if (campaignEnabled10Dlc === false && isSMSConversation) {
    return (
      <Styled.MessageInputAreaDisabled>
        <Styled.BodyCopy>
          Your number is not enabled for SMS messaging. Contact your account
          admin to register it.
        </Styled.BodyCopy>
      </Styled.MessageInputAreaDisabled>
    );
  }

  // Early return when no conversation is available (allow new conversation page)
  if (
    !(
      conversation?.CurrentConversation ||
      isCreatingAdhocGroup ||
      routeChecks.isNewConversationPage()
    )
  ) {
    return null;
  }

  return <MessageInputArea />;
});
